import * as stylex from "@stylexjs/stylex";
import { colors, spacing } from "@softmax/design-system/tokens.stylex";
import type { MouseEventHandler, ReactElement, ReactNode } from "react";
import { SiteNavigationGroup as DrawerGroup } from "./site-navigation-group";
import { SiteNavigationAction } from "./site-navigation-action";
import { SiteNavigationDoor } from "./site-navigation-door";
import type {
  SiteNavigationGroup,
  SiteNavigationLink,
  SiteNavigationLinkProps,
} from "./site-navigation-types";

export function SiteNavigationDrawer({
  menus,
  drawerDoors,
  action,
  account,
  renderSearch,
  renderLink,
  onNavigate,
  onClose,
}: {
  readonly menus: readonly SiteNavigationGroup[];
  readonly drawerDoors: readonly SiteNavigationLink[];
  readonly action: SiteNavigationLink | null;
  readonly account: ReactNode;
  readonly renderSearch: ((onNavigate: () => void) => ReactNode) | undefined;
  readonly renderLink: (props: SiteNavigationLinkProps) => ReactElement;
  readonly onNavigate: MouseEventHandler<HTMLAnchorElement>;
  readonly onClose: () => void;
}) {
  return (
    <div {...stylex.props(styles.drawer)}>
      {renderSearch && (
        <div {...stylex.props(styles.divided)}>{renderSearch(onClose)}</div>
      )}
      {drawerDoors.map((door) => (
        <div key={door.href} {...stylex.props(styles.divided)}>
          {
            <SiteNavigationDoor
              item={door}
              mobile
              renderLink={renderLink}
              onNavigate={onNavigate}
            />
          }
        </div>
      ))}
      {menus.map((menu) => (
        <DrawerGroup
          key={menu.label}
          menu={menu}
          renderLink={renderLink}
          onNavigate={onNavigate}
        />
      ))}
      <div {...stylex.props(styles.drawerAction)}>
        {account ?? (
          <SiteNavigationAction
            action={action}
            renderLink={renderLink}
            onClick={onNavigate}
          />
        )}
      </div>
    </div>
  );
}

const styles = stylex.create({
  drawer: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100%",
    paddingInline: 20,
  },
  divided: {
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    borderBottomColor: colors.border,
  },
  drawerAction: {
    marginTop: "auto",
    paddingBlock: spacing.space24,
    display: { default: "block", "@media (min-width: 380px)": "none" },
  },
});
